import React, { useEffect } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { v4 } from 'uuid'
import { l, TextInputV2 } from '@manychat/manyui'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { MetaInfo } from 'apps/aiAutomation/components/KnowledgeEditor/components/MetaInfo'
import { SourceTextEditor } from 'apps/aiAutomation/components/KnowledgeEditor/components/SourceTextEditor'
import { knowledgeEntryResolver } from 'apps/aiAutomation/lib/knowledge'
import { AiAutomationKnowledgeSelectors } from 'apps/aiAutomation/store/knowledge'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import { clearTextFromTags } from 'components/MCTextEditor/formatting/adapter'
import {
  KnowledgeEntryType,
  TextEntry,
  TextEntryDraft,
} from 'shared/api/requests/aiBusinessKnowledge/schemas'

import { EntryControls } from '../EntryControls'

interface TextEntryFormValues {
  title: string
  text: string
}

interface TextEntryEditorProps {
  entry: TextEntry | null
  onSubmit: (entry: TextEntry | TextEntryDraft) => void
  onBackToReadMode: () => void
  isLoading: boolean
}

export const TextEntryEditor = ({
  entry,
  onSubmit,
  isLoading,
  onBackToReadMode,
}: TextEntryEditorProps) => {
  const dispatch = useAppDispatch()

  const isNewEntry = Boolean(!entry)
  const isLoaded = useAppSelector((state) =>
    AiAutomationKnowledgeSelectors.getIsItemLoaded(state, entry ? entry.id : null),
  )

  const { handleSubmit, formState, register, setValue, control } = useForm<TextEntryFormValues>({
    defaultValues: {
      text: entry?.data?.text || '',
      title: entry?.title || '',
    },
    resolver: knowledgeEntryResolver,
    mode: 'onChange',
  })

  const submit: SubmitHandler<TextEntryFormValues> = ({ text, title }) => {
    if (entry) {
      onSubmit({
        ...entry,
        title,
        data: {
          ...entry.data,
          text: clearTextFromTags(text),
        },
      })
    } else {
      onSubmit({
        id: v4(),
        type: KnowledgeEntryType.TEXT,
        title,
        data: {
          text: clearTextFromTags(text),
        },
      } as TextEntryDraft)
    }
  }

  useEffect(() => {
    if (isLoaded && entry) {
      setValue('text', entry.data.text)
    }
  }, [isLoaded, entry, setValue])

  useEffect(() => {
    dispatch(
      AiAutomationLayoutActions.confirmChanged({
        show: formState.isDirty,
      }),
    )
  }, [dispatch, formState.isDirty])

  const { ref: titleRef, ...titleProps } = register('title')

  return (
    <>
      {!isNewEntry && <MetaInfo isNewEntry={isNewEntry} updatedAt={entry?.updated_at} />}

      <TextInputV2
        inputRef={titleRef}
        {...titleProps}
        errorText={formState.errors.title?.message}
        invalid={Boolean(formState.errors.title?.message)}
        readOnly={isLoading}
        className="m-b-lg"
        label={l.translate('What’s this about?')}
        data-test-id="ai-automation/knowledge-editor-title-input"
        placeholder={l.translate('e.g. Course curriculum')}
      />

      <Controller
        name="text"
        control={control}
        render={({ field, fieldState }) => (
          <SourceTextEditor
            className="m-b-lg"
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            readOnly={isLoading}
            invalid={fieldState.invalid}
            label={l.translate('Content')}
            disabled={isLoading}
            data-test-id="ai-automation/knowledge-editor-content-input"
            placeholder={l.translate(
              "Course format, what students learn, program length, pricing, and what's included...",
            )}
            errorText={fieldState.error?.message}
          />
        )}
      />

      <EntryControls
        entry={entry}
        onSubmit={handleSubmit(submit)}
        isLoading={isLoading}
        onBackToReadMode={onBackToReadMode}
      />
    </>
  )
}
